import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";

interface SelfieConsentGateProps {
  onConsented: () => void;
  onCancel: () => void;
}

const CONSENT_TYPE = "selfie_avatar";
// Pinned policy revision for the selfie/avatar consent. Mirrors the
// PRIVACY_POLICY_VERSION used at signup (Auth.tsx). Bump to force re-consent
// when the EULA §8.3 / Privacy Policy text materially changes (LGPD Art. 8 §5).
const POLICY_VERSION = "v1.0";

type Phase = "checking" | "prompt" | "saving";

export default function SelfieConsentGate({ onConsented, onCancel }: SelfieConsentGateProps) {
  const { t } = useLanguage();
  const [phase, setPhase] = useState<Phase>("checking");
  const [error, setError] = useState<string | null>(null);

  // On mount: if the user already consented under the current policy version,
  // skip straight through.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) setPhase("prompt");
          return;
        }
        const { data, error: qErr } = await (supabase as any)
          .from("user_consents")
          .select("id")
          .eq("user_id", user.id)
          .eq("consent_type", CONSENT_TYPE)
          .eq("policy_version", POLICY_VERSION)
          .limit(1);
        if (qErr) {
          console.error("[SelfieConsentGate] consent lookup failed:", qErr);
          if (!cancelled) setPhase("prompt");
          return;
        }
        if (!cancelled) {
          if (Array.isArray(data) && data.length > 0) onConsented();
          else setPhase("prompt");
        }
      } catch (err) {
        console.error("[SelfieConsentGate] consent check threw:", err);
        if (!cancelled) setPhase("prompt");
      }
    })();
    return () => { cancelled = true; };
  }, [onConsented]);

  const handleAccept = async () => {
    setPhase("saving");
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError(t("common.tryAgain"));
        setPhase("prompt");
        return;
      }
      const { error: insErr } = await (supabase as any)
        .from("user_consents")
        .insert({
          user_id: user.id,
          consent_type: CONSENT_TYPE,
          policy_version: POLICY_VERSION,
          user_agent: navigator.userAgent,
        });
      if (insErr) {
        console.error("[SelfieConsentGate] consent insert failed:", insErr);
        setError(t("common.tryAgain"));
        setPhase("prompt");
        return;
      }
      onConsented();
    } catch (err) {
      console.error("[SelfieConsentGate] consent insert threw:", err);
      setError(t("common.tryAgain"));
      setPhase("prompt");
    }
  };

  if (phase === "checking") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 px-1 py-2">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-foreground text-base">
            {t("metaperson.consent.title")}
          </p>
          <p className="text-[13px] text-muted-foreground mt-2 leading-relaxed">
            {t("metaperson.consent.body")}
          </p>
        </div>
      </div>

      {error && (
        <p className="text-destructive text-[12px] font-body">{error}</p>
      )}

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleAccept}
          disabled={phase === "saving"}
          className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-display font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 inline-flex items-center justify-center"
        >
          {phase === "saving" ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            t("metaperson.consent.authorize")
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={phase === "saving"}
          className="w-full h-11 rounded-xl text-sm font-display text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
        >
          {t("metaperson.consent.notNow")}
        </button>
      </div>
    </div>
  );
}
